import { useState, useEffect, useRef } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Loader, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { roomTypes, styleTypes } from "@/data/mockData";

const placeholderImage = "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=80&w=800&auto=format&fit=crop";
const pendingPreviewImage = "https://images.ctfassets.net/1z5g3o44yr4a/6Qw7Z1b6Qw7Z1b6Qw7Z1b6/6e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e/virtual-staging-pred-po.jpg";

const PROJECTS_PER_PAGE = 18;
const MAX_PROJECTS = 50;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const { user, loading: authLoading } = useAuth();
  const channelRef = useRef(null);

  // Debug logs
  console.log("[Projects] RENDER", { authLoading, user, loading });

  const fetchProjects = async () => {
    console.log("[Projects] fetchProjects called", { user });
    if (!user?.id) {
      console.log("[Projects] No user ID, clearing projects");
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      console.log("[Projects] Fetching projects for user:", user.id);
        
      // Add timeout to detect hanging queries
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Query timeout after 10s")), 10000);
      });

      // First get total count
      const { count, error: countError } = await supabase
        .from("projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (countError) {
        throw countError;
      }

      setTotalProjects(count || 0);

      // If total projects exceed MAX_PROJECTS, delete oldest ones
      if (count > MAX_PROJECTS) {
        const { error: deleteError } = await supabase
          .from("projects")
          .delete()
          .eq("user_id", user.id)
          .lt("created_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()) // Delete projects older than 30 days
          .order("created_at", { ascending: true })
          .limit(count - MAX_PROJECTS);

        if (deleteError) {
          console.error("[Projects] Error deleting old projects:", deleteError);
        }
      }

      // Then fetch paginated projects
      const queryPromise = supabase
        .from("projects")
        .select("id, original_image_url, staged_image_url, status, room_type, style_type, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .range((currentPage - 1) * PROJECTS_PER_PAGE, currentPage * PROJECTS_PER_PAGE - 1);
        
      const result = await Promise.race([queryPromise, timeoutPromise]) as { data: any, error: any };
      const { data, error } = result;
      
      console.log("[Projects] Supabase response:", { data, error });
      
      if (error) {
        console.error("[Projects] Error fetching projects:", error);
        toast.error("Failed to load projects");
        setProjects([]);
      } else {
        console.log("[Projects] Setting projects:", data);
        setProjects(data || []);
      }
    } catch (err) {
      console.error("[Projects] Exception in fetchProjects:", err);
      toast.error("Failed to load projects");
      setProjects([]);
    } finally {
      console.log("[Projects] Setting loading to false");
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("[Projects] useEffect", { authLoading, user });
    if (authLoading) return; // Wait for auth to finish loading

    if (!user?.id) {
      setProjects([]);
      setLoading(false);
      // Remove any existing subscription
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    setLoading(true);
    fetchProjects();
    
    // Set up real-time subscription
    const channel = supabase
      .channel('public:projects')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'projects',
          filter: `user_id=eq.${user.id}` 
        }, 
        (payload) => {
          console.log('[Projects] Project update received:', payload);
          fetchProjects();
        }
      )
      .subscribe();
      
    channelRef.current = channel;

    // Add visibilitychange handler to re-fetch projects when tab becomes active
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchProjects();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
      
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user, authLoading, currentPage]);

  const totalPages = Math.ceil(totalProjects / PROJECTS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatProjectTitle = (roomType: string, styleType: string) => {
    const room = roomTypes.find(r => r.id === roomType)?.name || roomType;
    const style = styleTypes.find(s => s.id === styleType)?.name || styleType;
    return `${room} - ${style}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8 flex items-center justify-center">
          <Loader className="h-8 w-8 animate-spin text-[#14A48D] mb-2" />
          <p>Načítání vašich projektů...</p>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8 flex items-center justify-center">
          <p>Pro zobrazení projektů se musíte přihlásit.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Moje projekty</h1>
          <Link to="/new-project">
            <Button>Nový projekt</Button>
          </Link>
        </div>
        {projects.length === 0 ? (
          <div className="text-center py-12 border rounded-lg">
            <h2 className="text-xl font-semibold mb-2">Zatím žádné projekty</h2>
            <p className="text-gray-500 mb-4">Začněte vytvořením nového projektu virtuálního zařízení</p>
            <Link to="/new-project">
              <Button>Vytvořit první projekt</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <Link key={project.id} to={`/projects/${project.id}`} className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                  <div className="aspect-video relative">
                    <img
                      src={
                        project.status === 'pending'
                          ? placeholderImage
                          : (["processing"].includes(project.status) && project.staged_image_url)
                            ? project.staged_image_url
                            : project.original_image_url || placeholderImage
                      }
                      alt="Náhled projektu"
                      className="w-full h-full object-cover"
                    />
                    {!project.original_image_url && project.status !== 'pending' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                        <span className="text-white font-bold">Bez původního obrázku</span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        project.status === 'completed' ? 'bg-green-100 text-green-700' : 
                        project.status === 'failed' ? 'bg-red-100 text-red-700' : 
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {project.status === 'completed' ? 'Dokončeno' :
                         project.status === 'failed' ? 'Selhalo' :
                         project.status === 'pending' ? 'Čeká' :
                         project.status === 'processing' ? 'Zpracování' :
                         project.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2">
                      {formatProjectTitle(project.room_type, project.style_type)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Vytvořeno: {new Date(project.created_at).toLocaleDateString('cs-CZ', {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Projects;
